// Custom single-select dropdown. Replaces the native <select> for the cascading
// Area/Sub-area/Item pickers so that long labels and options can be truncated
// with an ellipsis and constrained to the control width — native <select> popups
// are OS-rendered and cannot be styled (no ellipsis, no tooltip). The full text
// stays reachable via the `title` tooltip on both the trigger and each option.

import { useEffect, useId, useRef, useState } from 'react';

interface SelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  // Fires when focus leaves the whole widget (trigger + popup) — used for
  // blur-time validation ([공통] 00_입력검증표준 §1.2). Moving focus from the
  // trigger into the popup does NOT fire it.
  onBlur?: () => void;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  ariaLabel,
  onBlur,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const selectedIndex = options.indexOf(value);
  const hasValue = selectedIndex >= 0;

  // Close on outside click / Escape; keep the active option in view while open.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openMenu = () => {
    if (disabled) return;
    setActiveIndex(hasValue ? selectedIndex : 0);
    setOpen(true);
  };

  const choose = (index: number) => {
    const next = options[index];
    if (next === undefined) return;
    onChange(next);
    setOpen(false);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0) choose(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  return (
    <div
      className="ui-select"
      ref={wrapRef}
      // React's onBlur is focusout (it bubbles), so this catches focus leaving
      // any descendant. Ignore moves that stay inside the widget.
      onBlur={(e) => {
        if (!onBlur) return;
        const next = e.relatedTarget as Node | null;
        if (next && wrapRef.current?.contains(next)) return;
        onBlur();
      }}
    >
      <button
        type="button"
        className="ui-select__trigger"
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        title={hasValue ? value : undefined}
      >
        <span
          className={`ui-select__value${hasValue ? '' : ' ui-select__value--placeholder'}`}
        >
          {hasValue ? value : placeholder}
        </span>
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          className="ui-select__menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.length === 0 ? (
            <li className="ui-select__empty">—</li>
          ) : (
            options.map((opt, i) => (
              <li
                key={opt}
                role="option"
                aria-selected={opt === value}
                title={opt}
                className={`ui-select__option${
                  i === activeIndex ? ' ui-select__option--active' : ''
                }${opt === value ? ' ui-select__option--selected' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  // Prevent the trigger from losing focus before we handle the click.
                  e.preventDefault();
                  choose(i);
                }}
              >
                {opt}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
